/**
 * SA-946 (parent spec W16): who said what on a WhatsApp group thread. A group
 * message's body carries its sender (`from_name`, else `from_pn` / `from`) and
 * what it replies to (`reply_to`, the quoted message's WhatsApp id, and
 * `replied_to`, the sender and text WhatsApp carried with it); an attachment
 * is stored as its own message that names its body
 * (`whatsapp_provider_message_id`). Pure.
 */
import type { InboxMessage, TriageRow } from '@/types/inbox'
import { whenText } from '@/components/process/stepLiveText'

const str = (value: unknown): string | null => (typeof value === 'string' && value ? value : null)

/** The thread's group bodies by WhatsApp id (attachments carry none). */
export function indexByProviderId(messages: readonly InboxMessage[]): Map<string, InboxMessage> {
  const out = new Map<string, InboxMessage>()
  for (const message of messages) {
    const id = str(message.channelMetadata?.provider_message_id)
    if (id && !out.has(id)) out.set(id, message)
  }
  return out
}

/** The name above a group bubble: its sender's name, else (an attachment)
 * its message's, else the phone (`+` and digits) or the lid. */
export function senderName(message: InboxMessage, byId: Map<string, InboxMessage>): string | null {
  const own = message.channelMetadata ?? {}
  const body = str(own.whatsapp_provider_message_id)
  const meta = !str(own.from_name) && body ? (byId.get(body)?.channelMetadata ?? own) : own
  const phone = str(meta.from_pn)
  return str(meta.from_name) ?? (phone ? `+${phone}` : null) ?? str(meta.from)
}

export interface GroupQuote { sender: string | null; text: string }

/** One line of a message, cut at `cap`. */
export function excerpt(text: string | null | undefined, cap = 140): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim()
  return flat.length > cap ? `${flat.slice(0, cap - 1)}…` : flat
}

/** What a bubble replies to: the quoted message from this thread when it is
 * loaded, else what WhatsApp carried with the message; null when it quotes
 * nothing. */
export function quoteOf(message: InboxMessage, byId: Map<string, InboxMessage>): GroupQuote | null {
  const meta = message.channelMetadata ?? {}
  const replyTo = str(meta.reply_to)
  const quoted = replyTo ? byId.get(replyTo) : undefined
  if (quoted) return { sender: senderName(quoted, byId), text: excerpt(quoted.content) || 'An attachment' }
  const stored = meta.replied_to as { sender?: unknown; text?: unknown } | null | undefined
  const text = str(stored?.text)
  return text ? { sender: str(stored?.sender), text: excerpt(text) } : null
}

export interface GroupRowMessage {
  messageId: string
  sender: string | null
  excerpt: string
  /** SA-1329: when it was said (`whenText`). */
  when: string
  /** SA-1329: the sender of the message it replies to. */
  replyTo: string | null
  /** SA-1329: the message's words, trimmed, else "An attachment". */
  text: string
}

/** Each triage row's message, by ledger id; a row whose message the thread
 * did not load is left out. */
export function rowMessages(
  rows: ReadonlyArray<Pick<TriageRow, 'ledgerId' | 'providerMessageId'>>, byId: Map<string, InboxMessage>,
  zone?: string, now: Date = new Date(),
): Record<string, GroupRowMessage> {
  const out: Record<string, GroupRowMessage> = {}
  for (const row of rows) {
    const message = row.providerMessageId ? byId.get(row.providerMessageId) : undefined
    if (!message) continue
    out[row.ledgerId] = {
      messageId: message.id, sender: senderName(message, byId), excerpt: excerpt(message.content, 80) || 'An attachment',
      when: Number.isFinite(message.timestamp) ? whenText(new Date(message.timestamp).toISOString(), now, zone) ?? '' : '',
      replyTo: quoteOf(message, byId)?.sender ?? null,
      text: message.content?.trim() || 'An attachment',
    }
  }
  return out
}
